import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, latestUpdates } from '@/mocks/comics';

export default function ChapterReader() {
  const { slug, chapterId } = useParams<{ slug: string; chapterId: string }>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, [slug, chapterId]);

  const comic = useMemo(() => comics.find((c) => c.slug === slug), [slug]);
  const chNum = parseInt(chapterId || '1', 10);

  const allChapters = useMemo(
    () =>
      latestUpdates
        .filter((u) => u.comicSlug === slug)
        .sort((a, b) => b.chapterId - a.chapterId),
    [slug]
  );

  const currentChapter = allChapters.find((c) => c.chapterId === chNum) || {
    chapterTitle: `第${chNum}话`,
    chapterId: chNum,
  };

  const prevChapter = chNum > 1 ? chNum - 1 : null;
  const nextChapter = chNum < (comic?.chapters || 1) ? chNum + 1 : null;

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[70vh] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-bold text-foreground-900 mb-2">章节未找到</h2>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const readerImages = [
    'https://readdy.ai/api/search-image?query=korean%20webtoon%20vertical%20comic%20page%20scene%2C%20romantic%20dialogue%20between%20characters%2C%20clean%20line%20art%2C%20white%20background%2C%20professional%20webtoon%20style%2C%20Korean%20manhwa%20style&width=800&height=1200&seq=reader-01-p1&orientation=portrait',
    'https://readdy.ai/api/search-image?query=korean%20webtoon%20vertical%20comic%20page%20dramatic%20moment%2C%20character%20closeup%20expression%2C%20clean%20white%20background%2C%20professional%20manhwa%20art%20style&width=800&height=1200&seq=reader-01-p2&orientation=portrait',
    'https://readdy.ai/api/search-image?query=korean%20webtoon%20vertical%20comic%20panel%20storytelling%20scene%2C%20indoor%20setting%2C%20character%20interaction%2C%20clean%20style%2C%20Korean%20manhwa%20illustration&width=800&height=1200&seq=reader-01-p3&orientation=portrait',
    'https://readdy.ai/api/search-image?query=korean%20webtoon%20vertical%20comic%20page%20emotional%20cliffhanger%2C%20dramatic%20composition%2C%20clean%20line%20art%2C%20professional%20manhwa%20style%2C%20white%20background&width=800&height=1200&seq=reader-01-p4&orientation=portrait',
    'https://readdy.ai/api/search-image?query=korean%20webtoon%20vertical%20comic%20final%20page%2C%20resolution%20scene%2C%20soft%20art%20style%2C%20clean%20white%20background%2C%20Korean%20manhwa%20illustration&width=800&height=1200&seq=reader-01-p5&orientation=portrait',
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16 bg-background-950">
        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <Link
                to={`/comic/${comic.slug}`}
                className="text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </Link>
              <div className="min-w-0">
                <h1 className="text-sm font-semibold text-foreground-900 truncate">{comic.title}</h1>
                <p className="text-xs text-foreground-500 truncate">{currentChapter.chapterTitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {prevChapter && (
                <Link
                  to={`/comic/${comic.slug}/chapter/${prevChapter}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-foreground-600 bg-background-100 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>上一话
                </Link>
              )}
              {nextChapter && (
                <Link
                  to={`/comic/${comic.slug}/chapter/${nextChapter}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  下一话<i className="ri-arrow-right-s-line"></i>
                </Link>
              )}
            </div>
          </div>
        </div>

        <section className="max-w-2xl mx-auto px-2 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-3 h-3 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-3 h-3 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <p className="text-foreground-400 text-sm mt-4">加载中...</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {readerImages.map((img, i) => (
                <div key={i} className="w-full bg-background-800 rounded overflow-hidden">
                  <img
                    src={img}
                    alt={`${comic.title} - ${currentChapter.chapterTitle} 第${i + 1}页`}
                    title={`${comic.title} 无删减完整版下拉式漫画阅读`}
                    className="w-full h-auto object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-center gap-3 mt-8 pb-8">
            {prevChapter && (
              <Link
                to={`/comic/${comic.slug}/chapter/${prevChapter}`}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-foreground-600 bg-background-50 border border-background-300/60 rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-s-line"></i>上一话
              </Link>
            )}
            <Link
              to={`/comic/${comic.slug}`}
              className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium bg-background-50 border border-background-300/60 text-foreground-600 rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-list-check"></i>章节列表
            </Link>
            {nextChapter && (
              <Link
                to={`/comic/${comic.slug}/chapter/${nextChapter}`}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                下一话<i className="ri-arrow-right-s-line"></i>
              </Link>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}